// Service layer for Classified Ads feature.
// Wraps all /api/v1/off-page/classified-ads/* API calls.

import apiService from './api';

export interface Platform {
  name: string;
  raw_name: string;
  display: string;
  da: number;
  url: string;
}

export interface ClassifiedAdListing {
  id: string;
  platform: string;
  primary_keyword: string;
  title: string;
  description: string;
  word_count: number;
  status: 'draft' | 'pending' | 'live';
  listing_url: string | null;
  image_url: string | null;
  submitted_at: string | null;
  published_platforms?: string[];
  created_at: string;
}

export interface ClassifiedAdJob {
  id: string;
  user_id: string;
  keyword: string;
  selected_platforms: string[];
  status: 'processing' | 'completed' | 'failed';
  created_at: string;
  updated_at: string;
}

export interface PrefillData {
  brand_name: string;
  website_url: string;
  business_phone: string;
  business_address: string;
  business_category: string;
  keywords: string[];
  is_new_website?: boolean;
  domain_age_months?: number;
  products?: { name: string; url: string | null }[];
  services?: { name: string; url: string | null }[];
}

// Fetch static platform list
export async function getClassifiedAdPlatforms(): Promise<Platform[]> {
  const res = await apiService.request<{ status: string; data: Platform[] }>(
    '/off-page/classified-ads/platforms',
    { method: 'GET' }
  );
  return res.data;
}

// Get pre-populated NAP + keywords for the form
export async function getClassifiedAdPrefill(userId: string): Promise<PrefillData> {
  const res = await apiService.request<{ status: string; data: PrefillData }>(
    `/off-page/classified-ads/prefill/${encodeURIComponent(userId)}`,
    { method: 'GET' }
  );
  return res.data;
}

// Trigger Gemini AI generation for selected platforms (creates background job)
export async function generateClassifiedAdCopies(payload: {
  user_id: string;
  selected_platforms: string[];
  keyword: string;
}): Promise<ClassifiedAdJob> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdJob }>(
    '/off-page/classified-ads/generate',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return res.data;
}

// Fetch status of a specific background job
export async function getClassifiedAdJob(jobId: string): Promise<ClassifiedAdJob> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdJob }>(
    `/off-page/classified-ads/jobs/${encodeURIComponent(jobId)}`,
    { method: 'GET' }
  );
  return res.data;
}

// Fetch any active processing job for a user
export async function getActiveClassifiedAdJob(userId: string): Promise<ClassifiedAdJob | null> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdJob | null }>(
    `/off-page/classified-ads/jobs/active/${encodeURIComponent(userId)}`,
    { method: 'GET' }
  );
  return res.data;
}


// Fetch all listings for a user
export async function getClassifiedAdListings(userId: string): Promise<ClassifiedAdListing[]> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdListing[] }>(
    `/off-page/classified-ads/listings/${encodeURIComponent(userId)}`,
    { method: 'GET' }
  );
  return res.data;
}

// Fetch listings for a specific job
export async function getClassifiedAdListingsByJob(jobId: string): Promise<ClassifiedAdListing[]> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdListing[] }>(
    `/off-page/classified-ads/listings/job/${encodeURIComponent(jobId)}`,
    { method: 'GET' }
  );
  return res.data;
}

// Update live listing URL after manual publish.
// If `platform` is provided, the backend will duplicate the General listing
// under the specified platform slug (lazy duplication pattern).
export async function updateListingUrl(
  listingId: string,
  listingUrl: string,
  platform?: string,
): Promise<ClassifiedAdListing> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdListing }>(
    `/off-page/classified-ads/listings/${encodeURIComponent(listingId)}/url`,
    {
      method: 'PATCH',
      body: JSON.stringify({ listing_url: listingUrl, platform }),
    }
  );
  return res.data;
}

export async function updateListingContent(
  listingId: string,
  payload: { title?: string; description?: string }
): Promise<ClassifiedAdListing> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdListing }>(
    `/off-page/classified-ads/listings/${encodeURIComponent(listingId)}/content`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
  return res.data;
}

export async function completeClassifiedAdListing(
  listingId: string,
  payload: { platforms: string[] }
): Promise<ClassifiedAdListing> {
  const res = await apiService.request<{ status: string; data: ClassifiedAdListing }>(
    `/off-page/classified-ads/listings/${encodeURIComponent(listingId)}/complete`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return res.data;
}


// Download ZIP submission package
export async function downloadSubmissionPackage(
  userId: string,
  jobId?: string,
  listingId?: string
): Promise<void> {
  const token = localStorage.getItem('access_token') || '';
  const base = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
  let url = `${base}/off-page/classified-ads/download/${encodeURIComponent(userId)}`;
  const queryParams: string[] = [];
  if (jobId) queryParams.push(`job_id=${encodeURIComponent(jobId)}`);
  if (listingId) queryParams.push(`listing_id=${encodeURIComponent(listingId)}`);
  if (queryParams.length > 0) {
    url += `?${queryParams.join('&')}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) throw new Error('Failed to download submission package');

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = 'classified_ads_package.zip';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Returns a backend proxy URL for the AI-generated classified ad image.
 * The JWT token is embedded so the private S3 image can be loaded directly
 * in an <img> tag without requiring public bucket access or presigned URLs.
 */
export function getListingImageProxyUrl(listingId: string): string {
  const token = localStorage.getItem('access_token') || '';
  const base = (import.meta as any).env?.VITE_API_BASE_URL || '/api/v1';
  return `${base}/off-page/classified-ads/listings/${encodeURIComponent(listingId)}/image?token=${encodeURIComponent(token)}`;
}
